import { useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { differenceInMinutes } from "date-fns";
import {
  MailIcon,
  TriangleAlertIcon,
  Undo2Icon,
  UserMinusIcon,
} from "lucide-react";

import { formatDateRelative } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import type { ChallengeWithIdeaTemplate, OpenAPI } from "simplydo/interfaces";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { Button } from "simplydo/react/ui/button";
import { H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Popup } from "simplydo/react/ui/popup";
import { Tab } from "simplydo/react/ui/tab";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import ConfigurableTable from "components/lib/ConfigurableTable";

type UserAssessmentsModalProps = {
  assessorId: string;
  onClose: () => void;
  challenge: ChallengeWithIdeaTemplate;
  unsubmitAssessment: (
    ideaId: string,
    assessorId: string,
    assessmentRoundId: string,
  ) => void;
  unassignAssessor: (
    ideaId: string,
    assessorId: string,
    assessmentRoundId: string,
  ) => void;
  assessmentRoundId?: string;
};

const UserAssessmentsModal = ({
  assessorId,
  onClose,
  challenge,
  unsubmitAssessment,
  unassignAssessor,
  assessmentRoundId,
}: UserAssessmentsModalProps) => {
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();

  const ideaTemplate = challenge.ownerIdeaTemplate;
  const assessorQuery = useQuery(
    initUseQuery(api.challenges.getAssessor, [challenge._id, assessorId], {
      isEnabled: !!user,
    }),
  );

  const assessmentRounds = useMemo(() => {
    const availableRounds: string[] = [];
    assessorQuery.data?.ideas?.forEach((idea) => {
      idea.assessments?.forEach((assessment) => {
        if (assessment.user === assessorId && assessment.assessmentRoundId) {
          const roundId = assessment.assessmentRoundId;
          if (!availableRounds.includes(roundId)) {
            availableRounds.push(roundId);
          }
        }
      });
    });

    const allRounds = ideaTemplate?.assessments?.rounds || [];
    return allRounds.filter((round) => availableRounds.includes(round.id));
  }, [assessorQuery.data?.ideas, ideaTemplate, assessorId]);

  const [selectedAssessmentRoundId, setSelectedAssessmentRoundId] = useState<
    string | undefined
  >(assessmentRoundId);

  const remindAssessor = useCallback(
    (assessorId: string, assessmentRoundId: string) => {
      api.challenges.remindAssessor(
        challenge._id,
        assessorId,
        assessmentRoundId,
        () => {
          toast.success("Reminder sent to assessor");
          api.invalidateQueries(api.challenges.getAssessor, [
            challenge._id,
            assessorId,
          ]);
        },
        () => {
          toast.error("Failed to send reminder");
        },
      );
    },
    [challenge._id],
  );

  if (!ideaTemplate) return null;
  if (assessorQuery?.isLoading) return null;

  return (
    <Modal open onClose={onClose} fullscreen>
      <ModalHeader>
        <H3>
          Assessments for {assessorQuery.data?.assessor?.profile?.fullName}
        </H3>
      </ModalHeader>
      <ModalContent scrolling>
        <Tab
          variant="underline"
          activeIndex={assessmentRounds.findIndex(
            (round) =>
              round.id ===
              (selectedAssessmentRoundId || assessmentRounds[0]?.id),
          )}
          onTabChange={(_, data) => {
            const round = assessmentRounds[
              (data.activeIndex as number) || 0
            ] as any;
            setSelectedAssessmentRoundId(round?.id ?? null);
          }}
          panes={(assessmentRounds as any[]).map((round: any) => {
            return {
              menuItem: round.name,
              render: () => {
                const hasConflictOfInterest = assessorQuery.data?.ideas?.some(
                  (idea) =>
                    idea.assessments?.length &&
                    idea.assessments[0]?.conflictOfInterest
                      ?.hasConflictOfInterest,
                );

                const doneIdeas = assessorQuery.data?.ideas
                  ?.map((idea) => {
                    const { _id, name, assessments = [] } = idea;
                    const userAssessment = assessments.find((assessment) => {
                      return (
                        assessment?.assessmentRoundId ===
                        (selectedAssessmentRoundId || assessmentRounds[0]?.id)
                      );
                    });
                    if (!userAssessment) {
                      return null;
                    }

                    const absoluteScore = userAssessment?.assessment
                      ? Object.values(userAssessment.assessment)
                          .map((value) => value.score ?? 0)
                          .reduce((a, b) => a + b, 0)
                      : 0;
                    const absoluteTotalScore = (ideaTemplate.body as any[])
                      ?.flatMap((section: any) => {
                        if (
                          section.type === "assessment" &&
                          section.assessmentRoundId ===
                            (selectedAssessmentRoundId ||
                              assessmentRounds[0]?.id)
                        ) {
                          return section.fields
                            .filter((field: any) => field.type === "assessment")
                            .map((field: any) =>
                              Array.isArray(field.options)
                                ? 0
                                : (field?.options?.availableScore ?? 0),
                            );
                        }
                        return [];
                      })
                      .reduce((a: number, b: number) => a + b, 0);

                    return {
                      _id,
                      name,
                      absoluteScore,
                      absoluteTotalScore,
                      userAssessment,
                      idea,
                    };
                  })
                  .filter((x): x is NonNullable<typeof x> => x !== null);

                return (
                  <ConfigurableTable
                    data={(doneIdeas || []) as Record<string, any>[]}
                    tableKey="assessorIdeas"
                    columns={[
                      {
                        key: "image",
                        name: "",
                        settingName: "Image",
                        width: 25,
                        render: ({ item }) => (
                          <ImageWithFallback
                            style={{
                              width: 25,
                              height: 25,
                              objectFit: "cover",
                              borderRadius: 5,
                            }}
                            src={util.ideaCoverImage(
                              item?.idea as any,
                              "200x200",
                            )}
                            fallbackSrc={util.ideaCoverImage(undefined)}
                            alt={`${t("common:capitalise", { key: "generic.idea" })} cover`}
                          />
                        ),
                      },
                      {
                        key: "idea",
                        name: t("common:capitalise", { key: "generic.idea" }),
                        width: 400,
                        render: ({ item }) => (
                          <Link
                            to={`/ideas/${item._id}`}
                            style={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                            }}
                          >
                            {item.name}
                          </Link>
                        ),
                      },
                      {
                        name: "Status",
                        key: "status",
                        width: 100,
                        render: ({ item }) => {
                          return (
                            <>
                              {item?.userAssessment.startedAt ||
                              item?.userAssessment.isSubmitted ? (
                                item?.userAssessment.isSubmitted ? (
                                  <Label size="small" color="green">
                                    Submitted
                                  </Label>
                                ) : (
                                  <Label size="small" color="orange">
                                    In progress
                                  </Label>
                                )
                              ) : (
                                <Label size="small" color="red">
                                  Not started
                                </Label>
                              )}
                            </>
                          );
                        },
                      },
                      {
                        key: "conflictOfInterest",
                        name: "Conflict of Interest",
                        isVisible: !!hasConflictOfInterest,
                        width: 230,
                        render({ item }) {
                          return (
                            <div>
                              {item.userAssessment?.conflictOfInterest
                                ?.hasConflictOfInterest ? (
                                <div style={{ display: "flex" }}>
                                  <Popup
                                    trigger={
                                      <div>
                                        <Icon
                                          color="red"
                                          icon={TriangleAlertIcon}
                                        />
                                        <b>Conflict declared</b>
                                      </div>
                                    }
                                    disabled={
                                      !item?.userAssessment?.conflictOfInterest
                                        ?.notes
                                    }
                                  >
                                    {
                                      <>
                                        <b>Notes:</b>
                                        <br />
                                        &quot;
                                        {
                                          item?.userAssessment
                                            ?.conflictOfInterest?.notes
                                        }
                                        &quot;
                                      </>
                                    }
                                  </Popup>
                                </div>
                              ) : (
                                <i>-</i>
                              )}
                            </div>
                          );
                        },
                      },
                      {
                        name: "Total",
                        key: "total",
                        render: ({ item }) => {
                          return (
                            <>
                              {!item?.userAssessment?.conflictOfInterest
                                ?.hasConflictOfInterest ? (
                                <span>
                                  <strong>
                                    {item?.absoluteScore}/
                                    {item?.absoluteTotalScore}
                                  </strong>
                                </span>
                              ) : (
                                <i>-</i>
                              )}
                            </>
                          );
                        },
                      },
                      {
                        name: "Weighted %",
                        key: "weighted",
                        width: 100,
                        render: ({ item }) => {
                          return (
                            <>
                              {!item?.userAssessment?.conflictOfInterest
                                ?.hasConflictOfInterest ? (
                                <span>
                                  <strong>
                                    {Math.round(
                                      item?.userAssessment.score || 0,
                                    )}
                                    %
                                  </strong>
                                </span>
                              ) : (
                                <i>-</i>
                              )}
                            </>
                          );
                        },
                      },
                      ...(ideaTemplate.body as any[])
                        .filter(
                          (x: any) =>
                            x.type === "assessment" &&
                            x.assessmentRoundId ===
                              (selectedAssessmentRoundId ||
                                assessmentRounds[0]?.id),
                        )
                        .flatMap((section: any) => {
                          return section.fields
                            .filter(
                              (field: any) =>
                                field.type !== "infotext" &&
                                field.type !== "assessmentFeedback",
                            )
                            .map((field: any) => ({
                              key: field.id,
                              name:
                                field.title.substring(0, 30) +
                                (field.title.length > 30 ? "..." : ""),
                              compactTitle: true,
                              width: 150,
                              render: ({ item }: { item: any }) => {
                                if (
                                  item?.userAssessment?.conflictOfInterest
                                    ?.hasConflictOfInterest
                                ) {
                                  return <i>-</i>;
                                }
                                const userAssessment =
                                  item.userAssessment.assessment[field.id];
                                const sectionAvailableScore =
                                  field.options?.availableScore;

                                if (!userAssessment) {
                                  if (item?.userAssessment?.isSubmitted) {
                                    return (
                                      <i>
                                        This field was not part of the
                                        assessment at the time when this
                                        assessment was submitted. It is not
                                        considered in the final score.
                                      </i>
                                    );
                                  }
                                }

                                if (
                                  (userAssessment?.score &&
                                    !!userAssessment.score) ||
                                  (userAssessment?.value &&
                                    !!userAssessment.value)
                                ) {
                                  return (
                                    <Popup
                                      disabled={!userAssessment.notes}
                                      trigger={
                                        <p>
                                          {userAssessment.score
                                            ? `${userAssessment.score || 0}/${sectionAvailableScore}`
                                            : `${userAssessment.value}`}
                                        </p>
                                      }
                                    >
                                      {userAssessment?.notes}
                                    </Popup>
                                  );
                                }
                                if (
                                  userAssessment?.value &&
                                  !!userAssessment.value
                                ) {
                                  return <>{userAssessment.value}</>;
                                }
                              },
                            }));
                        }),
                    ]}
                    actions={
                      ((actionItems: Record<string, any>[]) =>
                        util.canManageChallenge(user, challenge) ||
                        util.hasPermission(
                          user,
                          "challenge.editAssessments",
                          challenge?._id,
                        )
                          ? [
                              {
                                icon: Undo2Icon,
                                name: "Unsubmit assessment",
                                isVisible:
                                  !!actionItems[0]?.userAssessment?.isSubmitted,
                                onClick: ([item]: Record<string, any>[]) =>
                                  unsubmitAssessment(
                                    item!._id,
                                    assessorId,
                                    (selectedAssessmentRoundId ||
                                      assessmentRounds[0]?.id) ??
                                      "",
                                  ),
                              },
                              {
                                type: "Divider",
                                isVisible:
                                  !!actionItems[0]?.userAssessment?.isSubmitted,
                              },
                              {
                                icon: MailIcon,
                                isVisible:
                                  !actionItems[0]?.userAssessment?.isSubmitted,
                                disabled:
                                  !!actionItems[0]?.userAssessment
                                    ?.lastContactedAt &&
                                  differenceInMinutes(
                                    new Date(),
                                    new Date(
                                      actionItems[0]?.userAssessment
                                        ?.lastContactedAt,
                                    ),
                                  ) < 1,
                                name: (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      flexDirection: "column",
                                    }}
                                  >
                                    Remind assessor
                                    <span
                                      style={{
                                        display: "block",
                                        color: "#888",
                                        fontSize: "0.9em",
                                      }}
                                    >
                                      Last contacted:{" "}
                                      {formatDateRelative(
                                        actionItems[0]?.userAssessment
                                          ?.lastContactedAt ||
                                          actionItems[0]?.userAssessment
                                            ?.createdAt,
                                        { addSuffix: true },
                                      )}
                                    </span>
                                  </span>
                                ),
                                onClick: (_: Record<string, any>[]) =>
                                  remindAssessor(
                                    assessorId,
                                    (selectedAssessmentRoundId ||
                                      assessmentRounds[0]?.id) ??
                                      "",
                                  ),
                              },
                              {
                                name: "Unassign from assessment",
                                checkPermission: "challenge.editAssessments",
                                checkPermissionScope: challenge._id,
                                onClick: ([item]: Record<string, any>[]) =>
                                  unassignAssessor(
                                    item!._id,
                                    assessorId,
                                    (selectedAssessmentRoundId ||
                                      assessmentRounds[0]?.id) ??
                                      "",
                                  ),
                                icon: UserMinusIcon,
                              },
                            ]
                          : []) as any
                    }
                  />
                );
              },
            };
          })}
        />
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose}>Close</Button>
      </ModalActions>
    </Modal>
  );
};

export default UserAssessmentsModal;
